import React, { useCallback, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { createOffer, listOffers, listPlans, updateOffer, type OfferInput } from "../../api/billing";
import { formatDateTime } from "../../shared/utils/format";
import { DataTable, LoadState, type Column } from "../UserDetails/ui";
import {
  NUDGE_LABEL,
  OFFER_NUDGE_KEYS,
  type AdminPlan,
  type OfferAudience,
  type OfferDisplay,
  type OfferNudgeKey,
  type OfferScope,
  type PlanOffer,
} from "./types";
import {
  Chip,
  ErrorText,
  Field,
  Modal,
  SaveButton,
  Toggle,
  fromLocalInput,
  inputClass,
  linkButtonClass,
  money,
  primaryButtonClass,
  secondaryButtonClass,
  toLocalInput,
  useAction,
  useLoad,
} from "./ui";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+){0,10}$/;
const TOKEN_HINT = "Placeholders: {{maxDiscount}}, {{daysLeft}}, {{endsAtDate}} — filled with real numbers.";

type ItemState = { basePlanId: number; discount: string; badge: string; selected: boolean };

const NOT_DISCOUNTABLE = new Set(["FREE", "ADDON", "ENTERPRISE"]);
const DEFAULT_DISCOUNT = "20";

/** Same rounding as the server (offerAdminService.discountedAmount): whole units rounded down, never below 1. */
const discountedAmount = (amount: number, pct: number) => Math.max(1, Math.floor((amount * (100 - pct)) / 100 + 1e-9));

const percentFromKey = (key: string | null | undefined): number | null => {
  const match = key ? /^PCT(\d{1,2})$/.exec(key) : null;
  return match ? Number(match[1]) : null;
};

const recurringPrices = (plan: AdminPlan) =>
  plan.prices.filter((p) => p.isActive && p.billingPeriod !== "one_time");

/** Paid, active, regular plans — the ones an offer can discount. */
function discountablePlans(plans: AdminPlan[]): AdminPlan[] {
  return plans.filter(
    (p) => p.basePlanId === null && p.isActive && !NOT_DISCOUNTABLE.has(p.category) && recurringPrices(p).length > 0
  );
}

/** The % an existing offer item gives, from its copy's key or its real prices. */
function existingDiscount(plans: AdminPlan[], basePlanId: number, variantKey: string): string {
  const fromKey = percentFromKey(variantKey);
  if (fromKey) return String(fromKey);
  const base = plans.find((p) => p.id === basePlanId);
  const copy = plans.find((p) => p.basePlanId === basePlanId && p.variantKey === variantKey);
  const bp = base && recurringPrices(base)[0];
  const vp = copy?.prices.find((p) => p.billingPeriod === bp?.billingPeriod && p.currency === bp?.currency);
  if (!bp || !vp || Number(bp.amount) <= 0) return DEFAULT_DISCOUNT;
  return String(Math.round((1 - Number(vp.amount) / Number(bp.amount)) * 100));
}

function initialItems(bases: AdminPlan[], plans: AdminPlan[], offer: PlanOffer | null): ItemState[] {
  return bases.map((base) => {
    const existing = offer?.plan_offer_items.find((i) => i.basePlanId === base.id);
    return {
      basePlanId: base.id,
      discount: existing ? existingDiscount(plans, base.id, existing.variantKey) : DEFAULT_DISCOUNT,
      badge: existing?.badge ?? "",
      selected: Boolean(existing),
    };
  });
}

const PERIOD_SHORT: Record<string, string> = { monthly: "/mo", yearly: "/yr", quarterly: "/qtr", weekly: "/wk" };

/** "₹2,399 → ₹1,919 /mo · ₹23,990 → ₹19,192 /yr" for the chosen %. */
const PricePreview: React.FC<Readonly<{ plan: AdminPlan; discount: string }>> = ({ plan, discount }) => {
  const pct = Number.parseInt(discount, 10);
  const valid = Number.isInteger(pct) && pct >= 1 && pct <= 90;
  return (
    <p className="flex flex-wrap gap-x-3 text-xs text-gray-500">
      {recurringPrices(plan).map((p) => (
        <span key={p.id} className="whitespace-nowrap">
          {valid && <span className="text-gray-400 line-through">{money(p.amount, p.currency)}</span>}{" "}
          <span className={valid ? "font-semibold text-gray-800" : ""}>
            {money(valid ? discountedAmount(Number(p.amount), pct) : p.amount, p.currency)}
          </span>
          {PERIOD_SHORT[p.billingPeriod] ?? ""}
        </span>
      ))}
    </p>
  );
};

function validateOffer(input: {
  slug: string;
  name: string;
  scope: OfferScope;
  startsAt: string;
  endsAt: string;
  windowHours: string;
  items: ItemState[];
}): string | null {
  if (!SLUG_PATTERN.test(input.slug)) return "Slug: lowercase words joined by dashes, e.g. diwali-2026.";
  if (!input.name.trim()) return "Name is required.";
  if (input.scope === "GLOBAL") {
    if (!input.startsAt || !input.endsAt) return "A sale needs a start and an end.";
    if (new Date(input.startsAt) >= new Date(input.endsAt)) return "The sale must end after it starts.";
  } else {
    const hours = Number.parseInt(input.windowHours, 10);
    if (!Number.isInteger(hours) || hours < 1 || hours > 720) return "Offer window must be 1–720 hours.";
  }
  const selected = input.items.filter((i) => i.selected);
  if (selected.length === 0) return "Pick at least one plan.";
  const badDiscount = selected.some((i) => {
    const pct = Number.parseInt(i.discount, 10);
    return !Number.isInteger(pct) || pct < 1 || pct > 90;
  });
  if (badDiscount) return "Discount must be a whole number from 1 to 90 for every chosen plan.";
  return null;
}

const OfferFormModal: React.FC<
  Readonly<{ offer: PlanOffer | null; plans: AdminPlan[]; onClose: () => void; onSaved: () => void }>
> = ({ offer, plans, onClose, onSaved }) => {
  const bases = useMemo(() => discountablePlans(plans), [plans]);
  const [slug, setSlug] = useState(offer?.slug ?? "");
  const [name, setName] = useState(offer?.name ?? "");
  const [scope, setScope] = useState<OfferScope>(offer?.scope ?? "GLOBAL");
  const [audience, setAudience] = useState<OfferAudience>(offer?.audience ?? "NON_PAID");
  const [nudgeKey, setNudgeKey] = useState<OfferNudgeKey>(offer?.nudgeKey ?? "FIRST_POST");
  const [startsAt, setStartsAt] = useState(toLocalInput(offer?.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInput(offer?.endsAt));
  const [windowHours, setWindowHours] = useState(String(offer?.windowHours ?? 48));
  const [maxRedemptions, setMaxRedemptions] = useState(offer?.maxRedemptions ? String(offer.maxRedemptions) : "");
  const [maxGrantsPerDay, setMaxGrantsPerDay] = useState(offer?.maxGrantsPerDay ? String(offer.maxGrantsPerDay) : "");
  const [display, setDisplay] = useState<OfferDisplay>(offer?.display ?? {});
  const [isActive, setIsActive] = useState(offer?.isActive ?? true);
  const [items, setItems] = useState<ItemState[]>(() => initialItems(bases, plans, offer));
  const [allDiscount, setAllDiscount] = useState(offer ? "" : DEFAULT_DISCOUNT);
  const { busy, error, setError, run } = useAction();

  const setItem = (basePlanId: number, patch: Partial<ItemState>) =>
    setItems((prev) => prev.map((i) => (i.basePlanId === basePlanId ? { ...i, ...patch } : i)));
  const applyToAll = (value: string) => {
    setAllDiscount(value);
    setItems((prev) => prev.map((i) => ({ ...i, discount: value })));
  };
  const setText = (field: keyof OfferDisplay, value: string) => setDisplay((prev) => ({ ...prev, [field]: value }));
  const optionalInt = (value: string) => (value.trim() ? Number.parseInt(value, 10) : null);

  const save = async () => {
    const problem = validateOffer({ slug, name, scope, startsAt, endsAt, windowHours, items });
    if (problem) return setError(problem);
    const payload: OfferInput = {
      name: name.trim(),
      scope,
      audience,
      nudgeKey: scope === "TARGETED" ? nudgeKey : null,
      startsAt: scope === "GLOBAL" ? fromLocalInput(startsAt) : null,
      endsAt: scope === "GLOBAL" ? fromLocalInput(endsAt) : null,
      windowHours: scope === "TARGETED" ? Number.parseInt(windowHours, 10) : null,
      maxRedemptions: optionalInt(maxRedemptions),
      maxGrantsPerDay: scope === "TARGETED" ? optionalInt(maxGrantsPerDay) : null,
      display,
      isActive,
      items: items
        .filter((i) => i.selected)
        .map((i, index) => ({
          basePlanId: i.basePlanId,
          discountPercent: Number.parseInt(i.discount, 10),
          badge: i.badge.trim() || null,
          sortOrder: index,
        })),
    };
    const saved = await run(() => (offer ? updateOffer(offer.id, payload) : createOffer({ ...payload, slug })));
    if (saved) onSaved();
  };

  return (
    <Modal
      title={offer ? `Edit offer · ${offer.slug}` : "New offer"}
      onClose={onClose}
      busy={busy}
      width="xl"
      footer={
        <>
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <SaveButton busy={busy} onClick={save}>
            {offer ? "Save offer" : "Create offer"}
          </SaveButton>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Slug" hint="Permanent id, e.g. diwali-2026.">
            <input className={inputClass} value={slug} disabled={!!offer} onChange={(e) => setSlug(e.target.value.trim().toLowerCase())} />
          </Field>
          <Field label="Name (internal)">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label="Type">
            <select className={inputClass} value={scope} onChange={(e) => setScope(e.target.value as OfferScope)}>
              <option value="GLOBAL">Sale — everyone, between two dates</option>
              <option value="TARGETED">Personal — given by a nudge, for some hours</option>
            </select>
          </Field>
          <Field label="Who can use it">
            <select className={inputClass} value={audience} onChange={(e) => setAudience(e.target.value as OfferAudience)}>
              <option value="NON_PAID">Anyone not on a paid plan (incl. past customers)</option>
              <option value="NEVER_PAID">Only people who never paid</option>
            </select>
          </Field>
          {scope === "GLOBAL" ? (
            <>
              <Field label="Starts">
                <input type="datetime-local" className={inputClass} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </Field>
              <Field label="Ends">
                <input type="datetime-local" className={inputClass} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </Field>
            </>
          ) : (
            <>
              <Field label="Given when">
                <select className={inputClass} value={nudgeKey} onChange={(e) => setNudgeKey(e.target.value as OfferNudgeKey)}>
                  {OFFER_NUDGE_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {NUDGE_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Valid for (hours)" hint="Countdown starts when the user gets it.">
                <input type="number" min={1} max={720} className={inputClass} value={windowHours} onChange={(e) => setWindowHours(e.target.value)} />
              </Field>
              <Field label="Max grants per day" hint="Optional budget guard.">
                <input type="number" min={1} className={inputClass} value={maxGrantsPerDay} onChange={(e) => setMaxGrantsPerDay(e.target.value)} />
              </Field>
            </>
          )}
          <Field label="Max purchases in total" hint="Optional, e.g. first 500 buyers.">
            <input type="number" min={1} className={inputClass} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Plans and discount</p>
            <Field label="Same discount % for all plans" className="w-56">
              <input type="number" min={1} max={90} className={inputClass} value={allDiscount} onChange={(e) => applyToAll(e.target.value)} />
            </Field>
          </div>
          {bases.length === 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">No active paid plans to discount yet.</p>
          )}
          {bases.map((base) => {
            const item = items.find((i) => i.basePlanId === base.id);
            if (!item) return null;
            const currency = recurringPrices(base)[0]?.currency.toUpperCase() ?? "";
            return (
              <div
                key={base.id}
                className={`grid grid-cols-1 gap-2 rounded-lg border p-3 md:grid-cols-[1.6fr_0.6fr_1fr] md:items-center ${
                  item.selected ? "border-violet-200 bg-violet-50/40" : "border-gray-100"
                }`}
              >
                <div className="min-w-0 space-y-1">
                  <Toggle
                    checked={item.selected}
                    onChange={(v) => setItem(base.id, { selected: v })}
                    label={`Include ${base.name} ${currency}`}
                    text={
                      <span className="font-medium text-gray-900">
                        {base.name} <span className="text-xs font-normal text-gray-400">{currency}</span>
                      </span>
                    }
                  />
                  {item.selected && <PricePreview plan={base} discount={item.discount} />}
                </div>
                <Field label="Discount %">
                  <input
                    type="number"
                    min={1}
                    max={90}
                    className={inputClass}
                    value={item.discount}
                    disabled={!item.selected}
                    onChange={(e) => setItem(base.id, { discount: e.target.value })}
                  />
                </Field>
                <Field label="Badge (optional)">
                  <input
                    className={inputClass}
                    placeholder="e.g. Best value"
                    maxLength={32}
                    value={item.badge}
                    disabled={!item.selected}
                    onChange={(e) => setItem(base.id, { badge: e.target.value })}
                  />
                </Field>
              </div>
            );
          })}
          <p className="text-xs text-gray-400">
            Saving creates the discounted prices with the payment provider (monthly and yearly). Buyers keep the
            discounted price for as long as they stay subscribed.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What users see</p>
          <p className="text-xs text-gray-400">{TOKEN_HINT}</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Badge">
              <input className={inputClass} maxLength={32} value={display.badge ?? ""} onChange={(e) => setText("badge", e.target.value)} placeholder="DIWALI SALE" />
            </Field>
            <Field label="Button label">
              <input className={inputClass} maxLength={40} value={display.ctaLabel ?? ""} onChange={(e) => setText("ctaLabel", e.target.value)} placeholder="See plans" />
            </Field>
            <Field label="Title" className="md:col-span-2">
              <input className={inputClass} maxLength={120} value={display.title ?? ""} onChange={(e) => setText("title", e.target.value)} placeholder="{{maxDiscount}}% off all plans" />
            </Field>
            <Field label="Subtitle" className="md:col-span-2">
              <input className={inputClass} maxLength={240} value={display.subtitle ?? ""} onChange={(e) => setText("subtitle", e.target.value)} placeholder="Only {{daysLeft}} days left. Ends {{endsAtDate}}." />
            </Field>
          </div>
        </div>

        <Toggle checked={isActive} onChange={setIsActive} label="Offer active" text={isActive ? "Active" : "Off"} />
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

const whenText = (o: PlanOffer) => {
  if (o.scope === "GLOBAL") return `${formatDateTime(o.startsAt)} → ${formatDateTime(o.endsAt)}`;
  const nudge = o.nudgeKey ? NUDGE_LABEL[o.nudgeKey] : "—";
  return `${nudge} · ${o.windowHours ?? "?"}h`;
};

/** Festival sales and personal nudge offers (DrawPost). */
export const OffersTab: React.FC = () => {
  const loadOffers = useCallback(() => listOffers(), []);
  const loadPlans = useCallback(() => listPlans("DRAWPOST_ADS"), []);
  const offers = useLoad(loadOffers);
  const plans = useLoad(loadPlans);
  const [editing, setEditing] = useState<PlanOffer | "new" | null>(null);
  const toggle = useAction();

  const setActive = async (offer: PlanOffer, isActive: boolean) => {
    const done = await toggle.run(() => updateOffer(offer.id, { isActive }));
    if (done) await offers.reload();
  };

  const columns: Column<PlanOffer>[] = [
    {
      header: "Offer",
      render: (o) => (
        <div>
          <p className="font-medium text-gray-900">{o.name}</p>
          <p className="text-xs text-gray-400">{o.slug}</p>
        </div>
      ),
    },
    { header: "Type", render: (o) => (o.scope === "GLOBAL" ? <Chip tone="violet">sale</Chip> : <Chip tone="amber">personal</Chip>) },
    { header: "When", render: whenText, className: "whitespace-nowrap" },
    {
      header: "Plans",
      render: (o) =>
        o.plan_offer_items
          .map((i) => {
            const plan = (plans.data ?? []).find((p) => p.id === i.basePlanId);
            const currency = plan ? recurringPrices(plan)[0]?.currency.toUpperCase() ?? "" : "";
            const name = plan?.name ?? `#${i.basePlanId}`;
            const discount = existingDiscount(plans.data ?? [], i.basePlanId, i.variantKey);
            return `${name} ${currency} −${discount}%`;
          })
          .join(", ") || "—",
    },
    { header: "Granted", render: (o) => (o.scope === "TARGETED" ? o._count?.user_offer_grants ?? 0 : "—") },
    {
      header: "Active",
      render: (o) => <Toggle checked={o.isActive} disabled={toggle.busy} label={`${o.slug} active`} onChange={(v) => void setActive(o, v)} />,
    },
    {
      header: "",
      render: (o) => (
        <button type="button" className={linkButtonClass} disabled={!plans.data} onClick={() => setEditing(o)}>
          Edit
        </button>
      ),
    },
  ];

  const loading = (offers.loading && !offers.data) || (plans.loading && !plans.data);
  const error = offers.error ?? plans.error;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">
          The best live offer wins per user (biggest discount, then soonest end). Paid subscribers never see offers.
        </p>
        <button type="button" className={primaryButtonClass} disabled={!plans.data} onClick={() => setEditing("new")}>
          <Plus className="w-4 h-4" /> New offer
        </button>
      </div>
      {toggle.error && <ErrorText message={toggle.error} />}
      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        {loading || error ? (
          <LoadState
            loading={loading}
            error={error}
            onRetry={() => {
              offers.reload();
              plans.reload();
            }}
          />
        ) : (
          <DataTable columns={columns} rows={offers.data ?? []} emptyText="No offers yet." />
        )}
      </div>
      {editing && plans.data && (
        <OfferFormModal
          offer={editing === "new" ? null : editing}
          plans={plans.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            offers.reload();
          }}
        />
      )}
    </div>
  );
};
